import React, { useEffect, useMemo, useState } from 'react';
import { Alert, AlertTitle, Box, IconButton, Link, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../services/api';

const STORAGE_KEY = 'dismissedAnnouncementsV2';

function readDismissedMap() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeDismissedMap(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {}
}

export default function PublicAnnouncement() {
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Fetch all pages to display all active announcements
        let page = 0;
        const size = 20;
        let all = [];
        let totalPages = 1;

        do {
          const { data } = await api.get('/v1/api/public-announcements?active=true', {
            params: { page, size, sort: 'updatedAt,desc' }
          });
          const items = Array.isArray(data?.content) ? data.content : [];
          all = all.concat(items);
          totalPages = Number.isFinite(data?.totalPages) ? data.totalPages : 1;
          page += 1;
        } while (page < totalPages && mounted);

        if (!mounted) return;
        setAnnouncements(all);
      } catch (e) {
        if (!mounted) return;
        setAnnouncements([]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const isDismissed = (a) => {
    const map = readDismissedMap();
    const version = a.updatedAt || a.endDate || a.id;
    return map[a.id] === version;
  };

  const dismissOne = (a) => {
    const map = readDismissedMap();
    const version = a.updatedAt || a.endDate || a.id;
    map[a.id] = version;
    writeDismissedMap(map);
    // Optimistically remove from local state
    setAnnouncements((prev) => prev.filter((x) => x.id !== a.id));
  };

  const visible = announcements.filter((a) => !isDismissed(a));

  if (!visible.length) return null;

  return (
    <Box sx={{
      position: 'sticky',
      top: 0,
      zIndex: (theme) => theme.zIndex.appBar - 1,
    }}>
      <Stack spacing={0}>
        {visible.map((a) => {
          const type = (a?.type || 'info').toString().toLowerCase();
          const severity = ['error','warning','info','success'].includes(type) ? type : 'info';
          const showTitle = Boolean(a.title);
          return (
            <Alert
              key={a.id}
              severity={severity}
              variant="filled"
              action={a.dismissible !== false && (
                <IconButton
                  aria-label="close announcement"
                  color="inherit"
                  size="small"
                  onClick={() => dismissOne(a)}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              )}
              sx={{
                borderRadius: 0,
                alignItems: 'center',
                '& .MuiAlert-message': { width: '100%' }
              }}
            >
              {/* {showTitle && <AlertTitle sx={{ fontWeight: 700 }}>{a.title}</AlertTitle>} */}
              <span dangerouslySetInnerHTML={{ __html: a.message }} />
              {a.link && (
                <>
                  {' '}
                  <Link href={a.link} color="inherit" underline="always" target="_blank" rel="noopener">
                    Learn more
                  </Link>
                </>
              )}
            </Alert>
          );
        })}
      </Stack>
    </Box>
  );
}
